import { AlgoliaSearchManager } from "../utils/algolia-search-research";

export async function initResearchDatabase() {
  // Made async
  const SEARCH_PARAM = "search"; // New constant for search parameter
  const CONTENT_TYPE_PARAM = "content_type"; // URL parameter for content type filter
  const FOCUS_AREA_PARAM = "focus_area"; // URL parameter for focus area filter

  const algoliaAppId = document.getElementById(
    "algolia-research-app-id",
  )?.value;
  const algoliaSearchKey = document.getElementById(
    "algolia-research-search-key",
  )?.value;
  const algoliaIndexName = document.getElementById(
    "algolia-research-index-name",
  )?.value;
  const algoliaInitialFilters =
    document.getElementById("algolia-research-initial-filters")?.value || null;
  const algoliaInitialSortByIndex =
    document.getElementById("algolia-research-initial-sort-by-index")?.value ||
    null;

  if (!algoliaAppId || !algoliaSearchKey || !algoliaIndexName) {
    console.error("Algolia credentials missing. Cannot initialize search.");
    return;
  }

  // Parse URL parameters BEFORE creating AlgoliaSearchManager to avoid triggering input events
  const urlParams = new URLSearchParams(window.location.search);
  const initialSearchTerm = urlParams.get(SEARCH_PARAM) || "";
  const initialContentType = urlParams.get(CONTENT_TYPE_PARAM) || "";
  const initialFocusArea = urlParams.get(FOCUS_AREA_PARAM) || "";

  const searchInput = document.getElementById("research-search");
  if (searchInput && initialSearchTerm) {
    searchInput.value = initialSearchTerm;
  }

  // Prevent Enter key from submitting/reloading the page
  if (searchInput) {
    searchInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
      }
    });
  }

  // Helper function to update URL with all current filter states
  function updateURL(searchTerm, contentType, focusArea) {
    const params = new URLSearchParams(window.location.search);

    if (searchTerm) {
      params.set(SEARCH_PARAM, searchTerm);
    } else {
      params.delete(SEARCH_PARAM);
    }

    if (contentType) {
      params.set(CONTENT_TYPE_PARAM, contentType);
    } else {
      params.delete(CONTENT_TYPE_PARAM);
    }

    if (focusArea) {
      params.set(FOCUS_AREA_PARAM, focusArea);
    } else {
      params.delete(FOCUS_AREA_PARAM);
    }

    const newURL = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    history.replaceState(null, null, newURL);
  }

  // Helper function to scroll to the research database
  function scrollToResearchDatabase() {
    // Use requestAnimationFrame to ensure DOM is ready and painted
    requestAnimationFrame(() => {
      // Try multiple selectors to find the research database container
      let researchDatabaseContainer =
        document.getElementById("research-database-block") ||
        document.querySelector('[data-block-name="acf/research-database"]') ||
        document
          .getElementById("algolia-research-search-container")
          ?.closest("section") ||
        document.getElementById("algolia-research-search-container");

      if (researchDatabaseContainer) {
        const prefersReducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)",
        ).matches;

        // Add a small delay to ensure layout is complete
        setTimeout(() => {
          researchDatabaseContainer.scrollIntoView({
            behavior: prefersReducedMotion ? "auto" : "smooth",
            block: "start",
          });
        }, 100);
      }
    });
  }

  const algoliaManager = new AlgoliaSearchManager(
    algoliaAppId,
    algoliaSearchKey,
    algoliaIndexName,
    "research-search",
    "algolia-research-results",
    "algolia-research-pagination",
    "algolia-research-results-count",
    "algolia-research-clear-button",
    algoliaInitialFilters,
    algoliaInitialSortByIndex, // Use the new initial sort index
    (query) => {
      // Callback from AlgoliaSearchManager when a search is triggered
      const researchContentTypeSelect = document.getElementById(
        "research-content-type",
      );
      const researchFocusAreaSelect = document.getElementById(
        "research-focus-area",
      );
      const currentContentType = researchContentTypeSelect?.value || "";
      const currentFocusArea = researchFocusAreaSelect?.value || "";
      updateURL(query, currentContentType, currentFocusArea);
    },
  );

  async function populateContentTypeDropdown() {
    const researchContentTypeSelect = document.getElementById(
      "research-content-type",
    );
    if (!researchContentTypeSelect) return;

    while (researchContentTypeSelect.options.length > 0) {
      researchContentTypeSelect.remove(0);
    }

    // Use embedded taxonomy data instead of fetching from API
    const terms = window.researchTaxonomies?.contentTypes || [];

    const anyContentTypeOption = document.createElement("option");
    anyContentTypeOption.value = "";
    anyContentTypeOption.textContent = "Filter by Type";
    researchContentTypeSelect.appendChild(anyContentTypeOption);

    buildHierarchicalOptions(terms, researchContentTypeSelect, 0);
    researchContentTypeSelect.value = "";
  }

  async function populateFocusAreaDropdown() {
    const researchFocusAreaSelect = document.getElementById(
      "research-focus-area",
    );
    if (!researchFocusAreaSelect) return;

    while (researchFocusAreaSelect.options.length > 0) {
      researchFocusAreaSelect.remove(0);
    }

    // Use embedded taxonomy data instead of fetching from API
    const terms = window.researchTaxonomies?.focusAreas || [];

    const anyFocusAreaOption = document.createElement("option");
    anyFocusAreaOption.value = "";
    anyFocusAreaOption.textContent = "Filter by Fund";
    researchFocusAreaSelect.appendChild(anyFocusAreaOption);

    // Show only top-level funds (no sub-levels)
    buildTopLevelOptions(terms, researchFocusAreaSelect);

    // COMMENTED OUT: To show hierarchical sub-levels, uncomment the line below and comment out the line above
    // buildHierarchicalOptions(terms, researchFocusAreaSelect, 0);

    researchFocusAreaSelect.value = "";
  }

  function buildTopLevelOptions(terms, selectElement) {
    // Only add top-level terms (no children/sub-levels)
    terms.forEach((term) => {
      const option = document.createElement("option");
      const decodedName = decodeHtmlEntities(term.name);
      option.value = decodedName;
      option.textContent = decodedName;
      selectElement.appendChild(option);
    });
  }

  function buildHierarchicalOptions(terms, selectElement, level) {
    terms.forEach((term) => {
      const option = document.createElement("option");
      const decodedName = decodeHtmlEntities(term.name);
      option.value = decodedName;
      const indent = level > 0 ? "— ".repeat(level) : "";
      option.textContent = indent + decodedName;
      selectElement.appendChild(option);

      if (term.children && term.children.length > 0) {
        buildHierarchicalOptions(term.children, selectElement, level + 1);
      }
    });
  }

  function decodeHtmlEntities(text) {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
  }

  const sortLinks = document.querySelectorAll(".js-sort-link");
  let currentActiveSortLink = document.querySelector(
    ".js-sort-link.sort-btn-active",
  );

  // Initialize the active sort link and indicator based on the default set in Twig
  if (currentActiveSortLink) {
    const initialIndicator = currentActiveSortLink.querySelector(
      ".sort-direction-indicator",
    );

    if (initialIndicator) {
      initialIndicator.style.display = "inline-block";
      initialIndicator.style.opacity = 1;
      // Check the data-current-dir attribute to set the initial rotation
      if (currentActiveSortLink.dataset.currentDir === "asc") {
        initialIndicator.classList.add("rotate-180");
      } else {
        initialIndicator.classList.remove("rotate-180");
      }
    }
  }

  sortLinks.forEach((link) => {
    link.addEventListener("click", function (e) {
      e.preventDefault();

      const indexName = this.dataset.indexName;
      let newDir = this.dataset.currentDir;
      let newSortByIndex;

      if (this.dataset.sortLabel === "Relevance") {
        newSortByIndex = indexName;
      } else {
        // It's the Date sort
        if (this === currentActiveSortLink) {
          newDir = newDir === "asc" ? "desc" : "asc";
        }
        newSortByIndex = `${indexName}_${newDir}`;
      }
      algoliaManager.setAlgoliaSortByIndex(newSortByIndex);

      this.dataset.currentDir = newDir;

      sortLinks.forEach((l) => {
        l.classList.remove("sort-btn-active");
        l.classList.add("sort-btn-inactive");
        const indicator = l.querySelector(".sort-direction-indicator");
        if (indicator) {
          indicator.style.opacity = 0;
          indicator.classList.remove("rotate-180");
        }
      });

      this.classList.add("sort-btn-active");
      this.classList.remove("sort-btn-inactive");

      const activeIndicator = this.querySelector(".sort-direction-indicator");
      if (activeIndicator) {
        activeIndicator.style.opacity = 1;
        if (newDir === "asc") {
          activeIndicator.classList.add("rotate-180");
        }
      }

      currentActiveSortLink = this;

      algoliaManager.performSearch(algoliaManager.currentQuery, 0);
    });
  });

  // Populate content type dropdown and perform initial search
  await populateContentTypeDropdown(); // Await content type dropdown population
  await populateFocusAreaDropdown();

  // Set initial filter values from URL after dropdowns are populated
  const researchContentTypeSelect = document.getElementById(
    "research-content-type",
  );
  const researchFocusAreaSelect = document.getElementById(
    "research-focus-area",
  );

  if (initialContentType && researchContentTypeSelect) {
    researchContentTypeSelect.value = initialContentType;
    algoliaManager.setAlgoliaFilter("content_type", initialContentType);
  }

  if (initialFocusArea && researchFocusAreaSelect) {
    researchFocusAreaSelect.value = initialFocusArea;
    algoliaManager.setAlgoliaFocusAreaFilter(initialFocusArea);
  }

  // Set up event listeners AFTER populating dropdowns to avoid triggering searches during initialization
  if (researchContentTypeSelect) {
    researchContentTypeSelect.addEventListener("change", function () {
      const selectedValue = this.value;
      algoliaManager.setAlgoliaFilter("content_type", selectedValue);
      algoliaManager.performSearch(algoliaManager.currentQuery, 0);

      // Update URL with current filter state
      const currentFocusArea = researchFocusAreaSelect?.value || "";
      updateURL(algoliaManager.currentQuery, selectedValue, currentFocusArea);
    });
  }

  if (researchFocusAreaSelect) {
    researchFocusAreaSelect.addEventListener("change", function () {
      const selectedValue = this.value;
      algoliaManager.setAlgoliaFocusAreaFilter(selectedValue);
      algoliaManager.performSearch(algoliaManager.currentQuery, 0);

      // Update URL with current filter state
      const currentContentType = researchContentTypeSelect?.value || "";
      updateURL(algoliaManager.currentQuery, currentContentType, selectedValue);
    });
  }

  const clearFilter = document.getElementById("algolia-research-clear-button");
  clearFilter.addEventListener("click", (e) => {
    e.preventDefault();

    // Reset UI filter elements to their default state
    const researchContentTypeSelect = document.getElementById(
      "research-content-type",
    );
    if (researchContentTypeSelect) {
      researchContentTypeSelect.value = "";
    }

    const researchFocusAreaSelect = document.getElementById(
      "research-focus-area",
    );
    if (researchFocusAreaSelect) {
      researchFocusAreaSelect.value = "";
    }

    // Clear Algolia's state and trigger a new search
    algoliaManager.clear(true);

    // Update the URL - clear all filter parameters
    updateURL("", "", "");
  });

  // Perform initial search after all setup is complete
  algoliaManager.performSearch(initialSearchTerm, 0);
  algoliaManager.showSearchResults();

  // Scroll to research database if there are any URL parameters
  if (initialSearchTerm || initialContentType || initialFocusArea) {
    scrollToResearchDatabase();
  }
}
